import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Shield, 
  Clock, 
  Users, 
  Award, 
  Star, 
  CheckCircle, 
  BookOpen, 
  Globe,
  Mail,
  ArrowRight,
  Target,
  Zap,
  Lock,
  Code,
  ExternalLink,
  Home,
  ArrowLeft
} from "lucide-react";

const CSCE = () => {
  const keyAreas = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Physical Security Technologies",
      description: "Integration of physical security systems with cyber infrastructure"
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Threat Modeling & Compliance",
      description: "Advanced threat assessment and regulatory compliance frameworks"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Protocol Integration",
      description: "OSDP, ONVIF, BACnet, and TLS protocol implementation"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Access Control Models",
      description: "RBAC, ABAC, and PBAC implementation and management"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Framework Alignment",
      description: "GDPR, ISO 27001, and NIS2 compliance strategies"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Cyber-Physical Integration",
      description: "Seamless integration of digital and physical security domains"
    }
  ];

  const benefits = [
    "Essential cybersecurity knowledge for converged environments",
    "Practical skills for integrated physical and digital security",
    "Foundation for career transition into converged security roles",
    "Collaboration readiness with IT teams and hybrid systems",
    "Industry-recognized credential from leading experts",
    "Digital badge for professional networking and verification"
  ];

  const idealFor = [
    "Physical security professionals transitioning to converged roles",
    "Facilities and operations staff entering cybersecurity",
    "IT professionals expanding into physical security integration",
    "Security managers overseeing hybrid environments",
    "Compliance officers working with converged systems",
    "Anyone starting their journey in converged security"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-pink-600 to-purple-700 text-white py-16 relative">
        {/* Navigation Bar - Upper Right */}
        <div className="absolute top-4 right-6 flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            onClick={() => window.open('https://convergedsecurity.es/', '_blank')}
          >
            <Home className="w-4 h-4 mr-1" />
            Home
          </Button>
        </div>
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-white/20 text-white border-white/30 mb-4">
                Foundational Certification
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                CSCE
              </h1>
              <h2 className="text-2xl md:text-3xl font-semibold mb-4 text-pink-100">
                Converged Security & Cybersecurity Expert
              </h2>
              <p className="text-xl text-pink-100 mb-8 leading-relaxed">
                Essential cybersecurity knowledge and practical skills for integrated physical and digital security environments. Perfect for professionals entering converged security roles.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="bg-white text-pink-600 hover:bg-pink-50"
                  onClick={() => window.open('mailto:info@convergedsecurity.es?subject=CSCE Certification Application&body=Hello, I would like to apply for the CSCE (Converged Security & Cybersecurity Expert) certification program. Please send me the application details and next steps.', '_blank')}
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Apply Now
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10"
                  onClick={() => window.open('https://pay.sumup.com/b2c/QI2N27DA', '_blank')}
                >
                  <Star className="w-5 h-5 mr-2" />
                  Pay Now - €250
                </Button>
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                  onClick={() => window.open('/#/csce-embed-docs', '_blank')}
                >
                  <Code className="w-4 h-4 mr-2" />
                  Get Embed Code
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                  onClick={() => window.open('/#/csce-embed', '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Preview Embed
                </Button>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <img 
                  src="./images/csce.png" 
                  alt="CSCE Certification Badge"
                  className="w-80 h-80 object-contain drop-shadow-2xl"
                />
                <div className="absolute -inset-4 bg-gradient-to-r from-pink-400/20 to-purple-400/20 rounded-full blur-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Facts */}
      <div className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-6">
            <Card className="text-center border-pink-200">
              <CardContent className="pt-6">
                <Star className="w-8 h-8 text-pink-600 mx-auto mb-3" />
                <h3 className="font-bold text-2xl text-gray-900">€250</h3>
                <p className="text-gray-600">Certification Fee</p>
              </CardContent>
            </Card>
            <Card className="text-center border-pink-200">
              <CardContent className="pt-6">
                <Clock className="w-8 h-8 text-pink-600 mx-auto mb-3" />
                <h3 className="font-bold text-2xl text-gray-900">3 Years</h3>
                <p className="text-gray-600">Validity Period</p>
              </CardContent>
            </Card>
            <Card className="text-center border-pink-200">
              <CardContent className="pt-6">
                <Users className="w-8 h-8 text-pink-600 mx-auto mb-3" />
                <h3 className="font-bold text-2xl text-gray-900">No Prerequisites</h3>
                <p className="text-gray-600">Entry Level</p>
              </CardContent>
            </Card>
            <Card className="text-center border-pink-200">
              <CardContent className="pt-6">
                <Award className="w-8 h-8 text-pink-600 mx-auto mb-3" />
                <h3 className="font-bold text-2xl text-gray-900">24 Hours</h3>
                <p className="text-gray-600">Badge Delivery</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Key Learning Areas */}
      <div className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What You'll Master
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive curriculum covering essential areas of converged security and cybersecurity integration
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {keyAreas.map((area, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300 border-pink-100">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-pink-100 rounded-lg text-pink-600">
                      {area.icon}
                    </div>
                    <CardTitle className="text-lg">{area.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{area.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Program Benefits */}
      <div className="py-16 bg-gradient-to-r from-pink-600 to-purple-700 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Why Choose CSCE?
              </h2>
              <p className="text-xl text-pink-100 mb-8">
                The CSCE certification provides the foundational knowledge needed to excel in today's converged security landscape.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-pink-200 flex-shrink-0 mt-0.5" />
                    <p className="text-pink-100">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6">Program Highlights</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5 text-pink-200" />
                  <span>Comprehensive written assessment and exam</span>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-pink-200" />
                  <span>Led by Dr. Vladimir Bunic, PhD in Cybersecurity</span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-pink-200" />
                  <span>Digital certificate and badge, verifiable online</span>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-pink-200" />
                  <span>EU GDPR, ISO 27001, and NIS2 compliant</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ideal Candidates */}
      <div className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Perfect For
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              CSCE is designed for professionals from various backgrounds entering the converged security field
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {idealFor.map((candidate, index) => (
              <Card key={index} className="border-pink-100 hover:border-pink-300 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <ArrowRight className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">{candidate}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="py-16 bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join professionals worldwide who have chosen CSCE as their foundation in converged security
          </p>
          
          <Card className="border-pink-200 shadow-lg">
            <CardContent className="pt-8">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-pink-600 font-bold">1</span>
                  </div>
                  <h3 className="font-semibold mb-2">Apply</h3>
                  <p className="text-sm text-gray-600">Submit your application and pay the certification fee</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-pink-600 font-bold">2</span>
                  </div>
                  <h3 className="font-semibold mb-2">Learn</h3>
                  <p className="text-sm text-gray-600">Complete the comprehensive program and assessment</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-pink-600 font-bold">3</span>
                  </div>
                  <h3 className="font-semibold mb-2">Certified</h3>
                  <p className="text-sm text-gray-600">Receive your digital badge within 24 hours</p>
                </div>
              </div>
              
              <div className="flex flex-wrap justify-center gap-4">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700"
                  onClick={() => window.open('https://pay.sumup.com/b2c/QI2N27DA', '_blank')}
                >
                  <Star className="w-5 h-5 mr-2" />
                  Pay Now - €250
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-pink-300 text-pink-600 hover:bg-pink-50"
                  onClick={() => window.open('mailto:info@convergedsecurity.es?subject=CSCE Certification Application&body=Hello, I would like to apply for the CSCE (Converged Security & Cybersecurity Expert) certification program. Please send me the application details and next steps.', '_blank')}
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Contact Us
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-pink-300 text-pink-600 hover:bg-pink-50"
                  onClick={() => window.open('mailto:info@convergedsecurity.es?subject=CSCE Brochure Request&body=Hello, I would like to download the CSCE (Converged Security & Cybersecurity Expert) certification brochure. Please send me the detailed program information.', '_blank')}
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  Download Brochure
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white py-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-gray-400 mb-2">
            © 2025 Converged Security Institute (CSI). All rights reserved.
          </p>
          <p className="text-sm text-gray-500">
            CSCE certification adheres to European Union GDPR, Cybersecurity Act, and NIS Directive
          </p>
        </div>
      </div>
    </div>
  );
};

export default CSCE;